import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { fetch } from '@ngrx/router-store/data-persistence';
import { concat, first, isNumber, tail, toInteger } from 'lodash';
import {
  catchError,
  filter,
  map,
  merge,
  of,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';

import {
  ClientDocumentsActions,
  ClientDocumentsEffectsActions,
} from '@lockton-apps/clients/actions';
import { DashboardContactActions } from '@lockton-apps/dashboard/action';
import { EnquiryOverviewActions } from '@lockton-apps/enquiries/action';
import { PolicyDetailsActions } from '@lockton-apps/policies/action';
import { DocumentsApiActions } from '@lockton-apps/shared/action/documents';
import { FoldersApiActions } from '@lockton-apps/shared/action/folders';
import { QuotesApiActions } from '@lockton-apps/shared/action/quotes';
import {
  FolderRouterSelectors,
  PolicyRouterSelectors,
} from '@lockton-apps/shared/data-access/router';
import { DocumentCreate } from '@lockton-apps/shared/interfaces';
import { DocumentsUploadDialogService } from '@lockton-apps/shared/ui/document-upload-dialog';
import { FeedbackService } from '@lockton-apps/shared/ui/lui';

import { DocumentsService } from '../services/documents.service';
import {
  QuoteAddonsActions,
  QuoteOptionsActions,
} from '@lockton-apps/quotes/action';
import { NewPaymentsActions } from '@lockton-apps/payments/action';

@Injectable()
export class DocumentsEffects {
  private store = inject(Store);
  private actions$ = inject(Actions);
  private documentsService = inject(DocumentsService);
  private feedbackService = inject(FeedbackService);
  private uploadDialogService = inject(DocumentsUploadDialogService);

  private navigated$ = this.actions$.pipe(ofType(ROUTER_NAVIGATED));

  loadDocuments$ = createEffect(() =>
    merge(
      this.actions$.pipe(
        ofType(
          ClientDocumentsActions.init,
          ClientDocumentsEffectsActions.navigated
        ),
        concatLatestFrom(() =>
          this.store.select(FolderRouterSelectors.selectRoutedId)
        ),
        map(([, folderId]) => folderId)
      ),
      this.actions$.pipe(
        ofType(DashboardContactActions.folderSelected),
        map(({ folderId }) => folderId)
      )
    ).pipe(
      switchMap((folderId) => {
        if (!folderId) {
          return of(
            DocumentsApiActions.folderDocumentsLoadedSuccess({
              documents: [],
            })
          );
        }
        return this.documentsService.allForFolder(toInteger(folderId)).pipe(
          map((res) =>
            DocumentsApiActions.folderDocumentsLoadedSuccess({
              documents: res,
            })
          ),
          catchError((error) =>
            of(DocumentsApiActions.folderDocumentsLoadedFailure({ error }))
          )
        );
      })
    )
  );

  uploadDocuments$ = createEffect(() =>
    merge(
      this.uploadDialogService.documentsSubmitted$(),
      this.actions$.pipe(ofType(FoldersApiActions.addedSuccess))
    ).pipe(
      filter(({ documents }) => !!documents && !!documents.length),
      map(({ documents }) => documents),
      switchMap((documents) => {
        return this.documentsService
          .add(first(documents) as DocumentCreate)
          .pipe(
            map((document) =>
              DocumentsApiActions.documentUploadedSuccess({
                document,
                success: [document],
                queue: tail(documents),
              })
            )
          );
      })
    )
  );

  uploadNextDocumentInQueue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DocumentsApiActions.documentUploadedSuccess,
        DocumentsApiActions.documentUploadedFailure
      ),
      filter(({ queue }) => !!queue.length),
      switchMap(({ queue, success, failed }) =>
        this.documentsService.add(first(queue) as DocumentCreate).pipe(
          map((uploadedDocument) =>
            DocumentsApiActions.documentUploadedSuccess({
              document: uploadedDocument,
              success: concat(success || [], uploadedDocument),
              failed,
              queue: tail(queue),
            })
          ),
          catchError((error) =>
            of(
              DocumentsApiActions.documentUploadedFailure({
                error,
                success,
                failed: concat(failed || [], first(queue) as DocumentCreate),
                queue: tail(queue),
              })
            )
          )
        )
      )
    )
  );

  completeDocumentsUpload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentsApiActions.documentUploadedSuccess),
      filter(({ queue }) => queue.length === 0),
      switchMap(({ document, success, failed }) =>
        of(
          DocumentsApiActions.documentsUploadCompleted({
            success: concat(success || [], document),
            failed: failed || [],
          })
        )
      )
    )
  );

  showLoader$ = createEffect(
    () =>
      this.uploadDialogService
        .documentsSubmitted$()
        .pipe(tap(() => this.feedbackService.showLoadingOverlay())),
    { dispatch: false }
  );

  hideLoader$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentsApiActions.documentsUploadCompleted),
        tap(() => this.feedbackService.hideLoadingOverlay())
      ),
    { dispatch: false }
  );

  downloadDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ClientDocumentsActions.documentDownloaded,
        DashboardContactActions.documentSelected,
        PolicyDetailsActions.documentSelected
      ),
      switchMap(({ id, name }) => {
        return this.documentsService.download(id, name).pipe(
          map((file) => DocumentsApiActions.downloadSuccess({ file, name })),
          catchError((error) =>
            of(DocumentsApiActions.downloadFailure({ error }))
          )
        );
      })
    )
  );

  downloadDocumentSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentsApiActions.downloadSuccess),
        tap(({ name }) => {
          const message = name
            ? $localize`${name} is now downloading`
            : $localize`Your file is now downloading`;
          this.feedbackService.subtleMessage(message);
        })
      ),
    { dispatch: false }
  );

  loadAllTags$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DashboardContactActions.init,
        ClientDocumentsActions.init,
        PolicyDetailsActions.init,
        DocumentsApiActions.documentsUploadCompleted
      ),
      fetch({
        run: () => {
          return this.documentsService.allTags().pipe(
            map((res) =>
              DocumentsApiActions.loadTagsSuccess({
                tags: res,
              })
            )
          );
        },
        onError: (_, error) => {
          console.error('Error', error);
          return DocumentsApiActions.loadTagsFailure({ error });
        },
      })
    )
  );

  policyDetailsInit$ = this.actions$.pipe(
    ofType(PolicyDetailsActions.init),
    concatLatestFrom(() =>
      this.store
        .select(PolicyRouterSelectors.selectRoutedId)
        .pipe(filter(Boolean))
    ),
    map(([, policyId]) => policyId)
  );

  externalFeatureInitAndQuoteLoaded$ = this.actions$.pipe(
    ofType(
      EnquiryOverviewActions.init,
      QuoteOptionsActions.init,
      QuoteAddonsActions.init,
      NewPaymentsActions.init
    ),
    switchMap(() =>
      this.actions$.pipe(
        ofType(QuotesApiActions.loadQuoteSuccess),
        takeUntil(this.navigated$)
      )
    ),
    map(({ quote }) => quote.policyId),
    filter(Boolean)
  );

  quoteFeatureEnquiryNavigated$ = this.actions$.pipe(
    ofType(EnquiryOverviewActions.navigated),
    filter(({ quote }) => isNumber(quote.policyId)),
    map(({ quote }) => quote.policyId as number)
  );

  loadPolicyDocuments$ = createEffect(() =>
    merge(
      this.policyDetailsInit$,
      this.externalFeatureInitAndQuoteLoaded$,
      this.quoteFeatureEnquiryNavigated$
    ).pipe(
      switchMap((policyId) =>
        this.documentsService.allForPolicy(policyId).pipe(
          map((documents) =>
            DocumentsApiActions.loadPolicyDocumentsSuccess({ documents })
          ),
          catchError((error) =>
            of(DocumentsApiActions.loadPolicyDocumentsFailure({ error }))
          )
        )
      )
    )
  );

  updateDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ClientDocumentsActions.documentUpdated,
        PolicyDetailsActions.documentUpdated
      ),
      switchMap(({ document }) => {
        return this.documentsService.update(document).pipe(
          map(() => DocumentsApiActions.documentUpdatedSuccess()),
          catchError(() => of(DocumentsApiActions.documentUpdatedFailure()))
        );
      })
    )
  );

  deleteDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ClientDocumentsActions.documentDeleted,
        DashboardContactActions.documentDeleted,
        PolicyDetailsActions.documentDeleted
      ),
      switchMap(({ id }) => {
        return this.documentsService.delete(id).pipe(
          map(() => DocumentsApiActions.deletedSuccess()),
          catchError(() => of(DocumentsApiActions.deletedFailure()))
        );
      })
    )
  );

  updateDocumentSuccess = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentsApiActions.documentUpdatedSuccess),
        tap(() =>
          this.feedbackService.subtleMessage(
            $localize`The document has been updated`,
            {
              type: 'success',
            }
          )
        )
      ),
    { dispatch: false }
  );

  deleteDocumentSuccess = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentsApiActions.deletedSuccess),
        tap(() =>
          this.feedbackService.subtleMessage(
            $localize`The document has been deleted`,
            {
              type: 'success',
            }
          )
        )
      ),
    { dispatch: false }
  );

  moveDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientDocumentsActions.documentMoved),
      switchMap(({ id, folderId }) => {
        return this.documentsService
          .update({ id, documentFolderId: folderId })
          .pipe(
            map(() => DocumentsApiActions.documentUpdatedSuccess()),
            catchError(() => of(DocumentsApiActions.documentUpdatedFailure()))
          );
      })
    )
  );
}
